import { useState, useMemo, useRef, useEffect } from 'react';
import { useAsyncCallback } from 'react-async-hook';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { ParsedQuery } from 'query-string';

import { investorApiClient } from 'api';
import { useBasketProvider } from 'providers/basket';
import { useCurrencySelection } from 'providers/currency-selection';
import { ALL_CURRENCIES_VALUE } from 'components/filters/constants';
import {
  SecondaryDealFilterModal,
  SecondaryDealFilterPayload,
  SecondaryDealFilterValues,
  secondaryDealFiltersHelper,
} from 'components/filters/secondary-deal';
import { useQuery, useLoadMoreController } from 'hooks';
import { SecondaryDeal } from 'types/secondary-market';
import { EndOfTableMessage, ScrollToTopButton, TableLayout } from 'components';

import { TableFilters } from '../components';
import { SecondaryDealHeader } from './SecondaryDealHeader';
import { SecondaryDealRecord, SecondaryDealRecordSkeleton } from './SecondaryDealRecord';

interface Props {
  wrapperRef?: HTMLElement | null;
  tableLayout: boolean;
}

type QueryLike = ParsedQuery;

type SecondaryTabQuery = ParsedQuery;

const normalizeStringArray = (value: unknown): string[] | undefined => {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
  if (typeof value === 'string' && value.length > 0) return [value];
  return undefined;
};

const toSecondaryTabQuery = (parsedQuery: QueryLike): SecondaryTabQuery => {
  return {
    ...parsedQuery,
    currency: normalizeStringArray(parsedQuery.currency) ?? null,
  };
};

const resolveCurrencyFilter = (
  queryCurrency: unknown,
  selectedCurrency: string | undefined,
): string[] | undefined => {
  const normalizedCurrency = normalizeStringArray(queryCurrency);

  if (normalizedCurrency?.includes(ALL_CURRENCIES_VALUE)) return undefined;
  if (normalizedCurrency && normalizedCurrency.length > 0) return normalizedCurrency;

  return selectedCurrency ? [selectedCurrency] : undefined;
};

export const SecondaryMarketTab = ({ wrapperRef, tableLayout: withTableLayout }: Props) => {
  const { parsedQuery, push } = useQuery();
  const basket = useBasketProvider();
  const { selectedCurrency, currencyOptions, currenciesLoading, currenciesError } =
    useCurrencySelection();

  const tabQuery = useMemo(
    () => toSecondaryTabQuery(parsedQuery as unknown as QueryLike),
    [parsedQuery],
  );

  const [data, setData] = useState<SecondaryDeal[]>([]);
  const [continuationToken, setContinuationToken] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const fetchVersionRef = useRef(0);
  const previousSelectedCurrency = useRef<string | undefined>();
  const previousFetchedSelectedCurrency = useRef<string | undefined>();

  useEffect(() => {
    if (!selectedCurrency) {
      return;
    }

    const didContextChange = previousSelectedCurrency.current !== selectedCurrency;
    const queryCurrency = normalizeStringArray(tabQuery.currency);
    const isAllCurrenciesSelected = queryCurrency?.includes(ALL_CURRENCIES_VALUE) ?? false;
    const hasCurrencyFilter = (queryCurrency?.length ?? 0) > 0;
    const shouldUpdate = !isAllCurrenciesSelected && (didContextChange || !hasCurrencyFilter);

    if (shouldUpdate && !(queryCurrency?.length === 1 && queryCurrency[0] === selectedCurrency)) {
      push({
        ...tabQuery,
        currency: [selectedCurrency],
      });
    }

    previousSelectedCurrency.current = selectedCurrency;
  }, [push, selectedCurrency, tabQuery]);

  const fetchDeals = useAsyncCallback(async (query: SecondaryTabQuery, fromBeginning?: boolean) => {
    if (!fromBeginning && continuationToken === 'EOF') return;

    if (fromBeginning) {
      fetchVersionRef.current += 1;
      setData([]);
      setContinuationToken(null);
      setTotal(null);
    }

    const fetchVersion = fetchVersionRef.current;

    const currency = resolveCurrencyFilter(query.currency, selectedCurrency);
    const { currency: _currency, ...rest } = query;

    const { data } =
      await investorApiClient.investors.investorApiControllerGetSecondarymarketOffersByContinuationToken({
        ...rest,
        currency,
        continuationToken: fromBeginning ? undefined : continuationToken ?? undefined,
      });

    const mappedData: SecondaryDeal[] = data.data.map((shareSell) => {
      return {
        ...shareSell,
        id: shareSell.shareSale.id,
      };
    });

    if (fetchVersion !== fetchVersionRef.current) {
      return;
    }

    setData((state) => {
      const dataToMerge = fromBeginning ? mappedData : state.concat(mappedData);
      const uniqueDeals = new Map<string, SecondaryDeal>();

      for (const deal of dataToMerge) {
        uniqueDeals.set(deal.id, deal);
      }

      return Array.from(uniqueDeals.values());
    });
    setContinuationToken(data.continuationToken ?? null);
    setTotal((state) => (typeof state === 'number' ? state : data.total));
  });

  useDeepCompareEffect(() => {
    if (currenciesLoading) {
      return;
    }

    if (!selectedCurrency) {
      console.error(
        'Cannot load secondary market deals because no global currency is selected.',
        currenciesError,
      );
      return;
    }

    const queryCurrency = normalizeStringArray(tabQuery.currency);
    const isAllCurrenciesSelected = queryCurrency?.includes(ALL_CURRENCIES_VALUE) ?? false;
    const hasCurrencyFilter = (queryCurrency?.length ?? 0) > 0;
    const queryMatchesSelectedCurrency =
      queryCurrency?.length === 1 && queryCurrency[0] === selectedCurrency;
    const didCurrencyContextChange = previousFetchedSelectedCurrency.current !== selectedCurrency;

    if (
      !isAllCurrenciesSelected &&
      ((didCurrencyContextChange && !queryMatchesSelectedCurrency) || !hasCurrencyFilter)
    ) {
      return;
    }

    previousFetchedSelectedCurrency.current = selectedCurrency;
    fetchDeals.execute(tabQuery, true);
  }, [tabQuery, selectedCurrency, currenciesLoading, currenciesError]);

  useLoadMoreController({
    wrapperRef,
    loading: fetchDeals.loading,
    continuationToken,
    fetch: fetchDeals.execute,
    query: tabQuery,
  });

  const dataToDisplay = data.filter((shareSell) => {
    const inBasket = basket.secondary.deals.find((basketDeal) => {
      return basketDeal.id === shareSell.id;
    });

    return !inBasket;
  });

  return (
    <>
      <TableFilters<SecondaryDealFilterPayload, SecondaryDealFilterValues>
        components={{
          TableHeader: SecondaryDealHeader,
          AllFiltersModal: SecondaryDealFilterModal,
        }}
        helpers={secondaryDealFiltersHelper}
        initialFilters={tabQuery}
        resetValues={{
          currency: selectedCurrency ? [selectedCurrency] : [],
        }}
        onFiltersChange={push}
        contextCurrency={selectedCurrency}
        currencyOptions={currencyOptions}
        totalDeals={total}
        loading={fetchDeals.loading}
        tableLayout={withTableLayout}
      />

      <TableLayout
        components={{
          Record: SecondaryDealRecord,
          RecordSkeleton: SecondaryDealRecordSkeleton,
        }}
        data={dataToDisplay}
        basketDeals={[]}
        basketLoading={false}
        dataLoading={fetchDeals.loading}
        tableLayout={withTableLayout}
      />

      {continuationToken === 'EOF' && <EndOfTableMessage />}
      <ScrollToTopButton wrapperRef={wrapperRef} />
    </>
  );
};
